// skipcq: JS-W1028
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Select sentinel: user enters a custom IANA id. */
export const EVENT_TIMEZONE_OTHER = '__custom__';

/** Curated US zones; pair with custom IANA for worldwide. */
export const US_EVENT_TIMEZONES = [
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'US Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'US Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'US Hawaii (Honolulu)' },
];

export const DEFAULT_EVENT_TIMEZONE = 'America/New_York';

/**
 * @param {unknown} timeZoneInput
 * @returns {boolean}
 */
export function isValidIanaTimeZone(timeZoneInput) {
  const trimmedTimeZone = String(timeZoneInput ?? '').trim();
  if (!trimmedTimeZone) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmedTimeZone }).format();
    return true;
  } catch {
    return false;
  }
}

/**
 * Display calendar date in the event zone (noon wall time avoids DST date-shift for date-only).
 * @param {unknown} eventLocalDate YYYY-MM-DD
 * @param {unknown} eventTimezone IANA
 * @returns {string}
 */
export function formatEventDateTimeLabel(eventLocalDate, eventTimezone) {
  const trimmedLocalDate = String(eventLocalDate ?? '').trim();
  const trimmedTimeZone = String(eventTimezone ?? '').trim();
  if (!trimmedLocalDate || !trimmedTimeZone) return '';
  if (!isValidIanaTimeZone(trimmedTimeZone)) {
    return trimmedLocalDate;
  }
  try {
    const zonedDate = dayjs.tz(
      `${trimmedLocalDate} 12:00:00`,
      trimmedTimeZone,
    );
    if (!zonedDate.isValid()) {
      return trimmedLocalDate;
    }
    return zonedDate.format('MMM D, YYYY z');
  } catch {
    return trimmedLocalDate;
  }
}

/**
 * Calendar date in the event zone, no timezone suffix (e.g. "June 27, 2026").
 * @param {unknown} eventLocalDate
 * @param {unknown} eventTimezone
 * @returns {string}
 */
export function formatEventLocalDateOnlyLabel(eventLocalDate, eventTimezone) {
  const trimmedLocalDate = String(eventLocalDate ?? '').trim();
  const trimmedTimeZone = String(eventTimezone ?? '').trim();
  if (!trimmedLocalDate || !trimmedTimeZone) return '';
  if (!isValidIanaTimeZone(trimmedTimeZone)) {
    return trimmedLocalDate;
  }
  try {
    const zonedDate = dayjs.tz(
      `${trimmedLocalDate} 12:00:00`,
      trimmedTimeZone,
    );
    if (!zonedDate.isValid()) {
      return trimmedLocalDate;
    }
    return zonedDate.format('MMMM D, YYYY');
  } catch {
    return trimmedLocalDate;
  }
}

/**
 * "12:00 PM to 4:00 PM" in the event zone from HH:mm pairs on the same local date.
 * @param {unknown} eventLocalDate
 * @param {unknown} eventTimezone
 * @param {unknown} eventStartTime HH:mm
 * @param {unknown} eventEndTime HH:mm
 * @returns {string}
 */
export function formatEventLocalTimeRangeLabel(
  eventLocalDate,
  eventTimezone,
  eventStartTime,
  eventEndTime,
) {
  const trimmedLocalDate = String(eventLocalDate ?? '').trim();
  const trimmedTimeZone = String(eventTimezone ?? '').trim();
  const startHm = String(eventStartTime ?? '').trim();
  const endHm = String(eventEndTime ?? '').trim();
  if (!trimmedLocalDate || !trimmedTimeZone || !startHm || !endHm) {
    return '';
  }
  if (!isValidIanaTimeZone(trimmedTimeZone)) {
    return '';
  }
  try {
    const startZoned = dayjs.tz(
      `${trimmedLocalDate} ${startHm}:00`,
      trimmedTimeZone,
    );
    const endZoned = dayjs.tz(
      `${trimmedLocalDate} ${endHm}:00`,
      trimmedTimeZone,
    );
    if (!startZoned.isValid() || !endZoned.isValid()) {
      return '';
    }
    return `${startZoned.format('h:mm A')} to ${endZoned.format('h:mm A')}`;
  } catch {
    return '';
  }
}
